import { useEffect } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from 'react-router-dom';
import AdminLayout from './layouts/AdminLayout';
import { RequireAdmin } from './guards/RequireAdmin';
import { useAuthStore } from './stores/authStore';
import { useGameStore } from './stores/gameStore';
import { adminHomePath, CONTENT_MANAGER_ONLY } from './utils/adminAccess';
import { landingPathsFor } from './games/registry';
import { gameSurfaceRoutes } from './games/surfaceRoutes';
import Login from './routes/Login';
import DashboardPage from './routes/dashboard/Dashboard';
import UsersList from './routes/users/List';
import UserDetail from './routes/users/Detail';
import AdminUsersList from './routes/adminUsers/List';
import MatchesList from './routes/matches/List';
import MatchDetail from './routes/matches/Detail';
import MatchmakingQueuePage from './routes/matchmaking/Queue';
import PurchasesList from './routes/purchases/List';
import EconomySummary from './routes/economy/Summary';
import LeaderboardSnapshot from './routes/leaderboard/Snapshot';
import AuditList from './routes/audit/List';
import ConfigPage from './routes/config/ConfigPage';
import BroadcastsPage from './routes/broadcasts/BroadcastsPage';
import SupportMessagesPage from './routes/support/SupportMessagesPage';
import Moderation from './routes/moderation/Moderation';

/**
 * Index redirect — role-aware so a content manager (who has no dashboard)
 * lands on the active game's content landing (its admin manifest's
 * `landingPaths`) instead of being bounced through the dashboard guard.
 */
function HomeRedirect() {
  const adminUser = useAuthStore((s) => s.adminUser);
  const activeGameId = useGameStore((s) => s.activeGameId);
  return (
    <Navigate
      to={
        adminUser
          ? adminHomePath(adminUser.role, landingPathsFor(activeGameId))
          : '/login'
      }
      replace
    />
  );
}

const router = createBrowserRouter([
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/',
    // Layout shell: any authenticated admin may enter. content_manager (tier
    // 0) is admitted via allowRoles; viewer+ via the tier check. Per-area
    // guards below enforce the actual scope.
    element: (
      <RequireAdmin minRole="viewer" allowRoles={CONTENT_MANAGER_ONLY}>
        <AdminLayout />
      </RequireAdmin>
    ),
    children: [
      { index: true, element: <HomeRedirect /> },

      // --- Per-game surfaces (e.g. Word Clash's question bank), each with
      //     its own tier + surface gate (games/surfaceRoutes.tsx). ---
      ...gameSurfaceRoutes(),

      // --- Viewer-tier surfaces. content_manager (tier 0) is intentionally
      //     excluded — it has no business on dashboards, users, economy, or
      //     the audit log. ---
      {
        element: (
          <RequireAdmin minRole="viewer">
            <Outlet />
          </RequireAdmin>
        ),
        children: [
          { path: 'dashboard', element: <DashboardPage /> },
          { path: 'users', element: <UsersList /> },
          { path: 'users/:id', element: <UserDetail /> },
          { path: 'matches', element: <MatchesList /> },
          { path: 'matches/:id', element: <MatchDetail /> },
          // Queue drill-down is viewer-tier: usernames + win counts are
          // already viewer-visible via the leaderboard snapshot.
          { path: 'matchmaking', element: <MatchmakingQueuePage /> },
          { path: 'economy', element: <EconomySummary /> },
          { path: 'leaderboard', element: <LeaderboardSnapshot /> },
          { path: 'audit', element: <AuditList /> },
        ],
      },

      // --- Admin-tier surfaces. ---
      {
        element: (
          <RequireAdmin minRole="admin">
            <Outlet />
          </RequireAdmin>
        ),
        children: [
          // Purchases carry identified buyer rows (userId, username, price,
          // store transactionId), so they sit with /users at admin tier — NOT
          // with the viewer-tier dashboard they drill into.
          { path: 'purchases', element: <PurchasesList /> },
          { path: 'remote-config', element: <ConfigPage /> },
          { path: 'broadcasts', element: <BroadcastsPage /> },
          { path: 'support', element: <SupportMessagesPage /> },
          { path: 'moderation', element: <Moderation /> },
        ],
      },

      // --- Superadmin-only: admin account management. ---
      {
        element: (
          <RequireAdmin minRole="superadmin">
            <Outlet />
          </RequireAdmin>
        ),
        children: [{ path: 'admin-users', element: <AdminUsersList /> }],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
], {
  // Vite sets BASE_URL to the `base` config: '/admin/' in prod, '/' in dev.
  // React Router prefixes every path with it, so routes like '/login' resolve
  // at '/admin/login' in prod (matching where Express mounts the SPA) while
  // dev still works at root. Strip the trailing slash so RR doesn't double it.
  basename: import.meta.env.BASE_URL.replace(/\/$/, '') || '/',
});

export default function App() {
  const hydrate = useAuthStore((s) => s.hydrate);
  useEffect(() => {
    // hydrate() is async (it probes /admin-refresh) — fire-and-forget here
    // is intentional. The store transitions through `hydrating` so the
    // route guards + Login screen can show a neutral state until it
    // resolves; awaiting in useEffect would only delay the first render
    // without changing the outcome.
    void hydrate();
  }, [hydrate]);
  return <RouterProvider router={router} />;
}
